const HTML_HINT = /^\s*(<!doctype|<html[\s>])/i;

function viteEnv(name: string): string {
  try {
    const env = (import.meta as ImportMeta & { env?: Record<string, string | undefined> }).env;
    return env?.[name]?.trim() ?? "";
  } catch {
    return "";
  }
}

export function parentOrigin(): string {
  return (viteEnv("VITE_PARENT_ORIGIN") || "https://azurehomeloans.com.au").replace(/\/$/, "");
}

/** Dedicated RateCheck API origin. Empty means same-origin `/api/*` (Vite dev). */
export function apiOrigin(): string {
  const raw = viteEnv("VITE_API_ORIGIN");
  return raw ? raw.replace(/\/$/, "") : "";
}

export function apiUrl(path: string, origin = apiOrigin()): string {
  const p = path.startsWith("/") ? path : `/${path}`;
  if (!origin) return `/api${p}`;
  return `${origin}${p}`;
}

export function isJsonContentType(value: string | null | undefined): boolean {
  return (value ?? "").toLowerCase().includes("application/json");
}

export function looksLikeHtml(text: string): boolean {
  return HTML_HINT.test(text);
}

export class ApiResponseError extends Error {
  readonly kind: "html" | "not_json" | "malformed" | "http" | "network" | "timeout";

  constructor(kind: ApiResponseError["kind"], message = kind) {
    super(message);
    this.kind = kind;
  }
}

export async function readApiJson<T>(res: Response): Promise<T> {
  const ct = res.headers.get("content-type");
  const text = await res.text();
  if (looksLikeHtml(text) || (!isJsonContentType(ct) && looksLikeHtml(text))) {
    throw new ApiResponseError("html");
  }
  if (!isJsonContentType(ct)) {
    throw new ApiResponseError("not_json");
  }
  if (looksLikeHtml(text)) {
    throw new ApiResponseError("html");
  }
  try {
    return JSON.parse(text) as T;
  } catch {
    throw new ApiResponseError("malformed");
  }
}

export function fetchWithTimeout(
  input: string,
  init: RequestInit = {},
  ms = 12_000,
  fetchImpl: typeof fetch = fetch,
): Promise<Response> {
  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), ms);
  const parent = init.signal;
  if (parent) {
    if (parent.aborted) ctrl.abort();
    else parent.addEventListener("abort", () => ctrl.abort(), { once: true });
  }
  return fetchImpl(input, { ...init, signal: ctrl.signal }).finally(() => clearTimeout(timer));
}
